import React, { useState, useEffect } from 'react';
import { apiClient } from '../api/client';
import { exportToCsv, buildCsvFilename } from '../utils/csvExport';

// ── Types ─────────────────────────────────────────────────────

type TabKey = 'lists' | 'analytics';
type ReportKey = 'members' | 'vendors' | 'sponsors' | 'users';

interface MemberRecord {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  chapterAffiliation: string | null;
  status: string;
  paidStatus: boolean;
  executiveTitle?: { name: string } | null;
  subcommitteeAffiliations: Array<{
    subcommitteeName: string;
    role: string;
  }>;
}

interface VendorRecord {
  id: string;
  businessName: string;
  contactPerson: string;
  email: string;
  phone: string | null;
  category: string | null;
  eventRegistrations?: Array<{
    event: { name: string };
    status: string;
    payments: Array<{ amount: number }>;
  }>;
}

interface SponsorRecord {
  id: string;
  sponsorName: string;
  contactPerson: string;
  tier: string;
  pledgedAmount: number;
  receivedAmount: number;
  events?: Array<{
    event: { name: string };
  }>;
}

interface UserRecord {
  id: string;
  fullName: string;
  email: string;
  role: string;
  createdAt?: string;
}

interface AnalyticsData {
  fundraising: {
    goalAmount: number;
    totalRaised: number;
    progressPercent: number;
    byType: Array<{ type: string; amount: number }>;
    activities: Array<{
      id: string;
      name: string;
      type: string;
      goalAmount: number;
      raised: number;
    }>;
  };
  events: Array<{
    id: string;
    name: string;
    date: string;
    registrationCount: number;
    maxCapacity: number;
    utilizationPercent: number;
  }>;
  financial: {
    ticketRevenue: number;
    vendorRevenue: number;
    sponsorRevenue: number;
    bookletRevenue: number;
    fundraisingRevenue: number;
    grandTotal: number;
  };
  membership: {
    activeCount: number;
    inactiveCount: number;
    duesCollectionRate: number;
    byChapter: Array<{ chapter: string; count: number }>;
  };
  committees: Array<{
    id: string;
    name: string;
    totalTasks: number;
    completedTasks: number;
    completionPercent: number;
    overdueTasks: number;
  }>;
}

const TABS: { key: TabKey; label: string }[] = [
  { key: 'lists', label: 'Reference Lists' },
  { key: 'analytics', label: 'Analytics' },
];

const REPORTS: { key: ReportKey; label: string; icon: string }[] = [
  { key: 'members', label: 'Members', icon: '👥' },
  { key: 'vendors', label: 'Vendors', icon: '🏪' },
  { key: 'sponsors', label: 'Sponsors', icon: '🤝' },
  { key: 'users', label: 'Users', icon: '👤' },
];

const TIER_RANK: Record<string, number> = {
  Platinum: 5,
  Gold: 4,
  Silver: 3,
  Bronze: 2,
  Custom: 1,
};

// ── Styles ────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: {
    padding: '2rem',
    maxWidth: '1200px',
    margin: '0 auto',
    backgroundColor: '#f5f7f5',
    minHeight: '100vh',
  },
  header: {
    marginBottom: '1.5rem',
  },
  title: {
    color: '#1a6b3c',
    fontSize: '1.75rem',
    margin: '0 0 0.5rem 0',
    fontWeight: 700,
  },
  description: {
    color: '#555',
    fontSize: '0.95rem',
    margin: 0,
    lineHeight: 1.5,
  },
  tabBar: {
    display: 'flex',
    gap: '0',
    marginBottom: '1.5rem',
    borderBottom: '2px solid #e0e0e0',
  },
  tab: {
    padding: '0.75rem 1.25rem',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    backgroundColor: 'transparent',
    color: '#666',
    borderBottom: '3px solid transparent',
    marginBottom: '-2px',
    transition: 'color 0.15s, border-color 0.15s',
  },
  tabActive: {
    padding: '0.75rem 1.25rem',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    backgroundColor: 'transparent',
    color: '#1a6b3c',
    borderBottom: '3px solid #1a6b3c',
    marginBottom: '-2px',
  },
  reportSelector: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1.5rem',
    flexWrap: 'wrap' as const,
  },
  reportButton: {
    padding: '0.5rem 1rem',
    fontSize: '0.85rem',
    fontWeight: 500,
    cursor: 'pointer',
    border: '1px solid #d0e8d6',
    borderRadius: '6px',
    backgroundColor: '#eef6f0',
    color: '#1a6b3c',
    transition: 'background-color 0.15s, border-color 0.15s',
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
  },
  reportButtonActive: {
    padding: '0.5rem 1rem',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
    border: '1px solid #1a6b3c',
    borderRadius: '6px',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
  },
  toolbar: {
    display: 'flex',
    gap: '0.75rem',
    marginBottom: '1.5rem',
    alignItems: 'center',
  },
  printButton: {
    padding: '0.35rem 0.5rem',
    fontSize: '0.9rem',
    cursor: 'pointer',
    border: 'none',
    borderRadius: '4px',
    backgroundColor: 'transparent',
    color: '#1a6b3c',
    display: 'flex',
    alignItems: 'center',
    transition: 'opacity 0.15s',
  },
  exportButton: {
    padding: '0.35rem 0.5rem',
    fontSize: '0.9rem',
    cursor: 'pointer',
    border: 'none',
    borderRadius: '4px',
    backgroundColor: 'transparent',
    color: '#c9a84c',
    display: 'flex',
    alignItems: 'center',
    transition: 'opacity 0.15s',
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  section: {
    marginBottom: '2rem',
    padding: '1.5rem',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  sectionTitle: {
    color: '#0f2d1f',
    fontSize: '1.2rem',
    marginBottom: '1rem',
    borderBottom: '2px solid #c9a84c',
    paddingBottom: '0.25rem',
    fontWeight: 600,
  },
  placeholder: {
    textAlign: 'center' as const,
    padding: '3rem',
    color: '#666',
    fontSize: '0.95rem',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.85rem',
    lineHeight: 1.4,
  },
  th: {
    padding: '0.6rem 0.75rem',
    textAlign: 'left' as const,
    backgroundColor: '#f0f7f2',
    borderBottom: '2px solid #1a6b3c',
    fontWeight: 600,
    color: '#0f2d1f',
    whiteSpace: 'nowrap' as const,
  },
  td: {
    padding: '0.5rem 0.75rem',
    borderBottom: '1px solid #e8e8e8',
    color: '#333',
    verticalAlign: 'top' as const,
  },
  trHover: {
    backgroundColor: '#fafcfa',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: '#666',
    fontSize: '0.95rem',
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: '#888',
    fontSize: '0.9rem',
    fontStyle: 'italic' as const,
  },
  outstandingBalance: {
    color: '#c0392b',
    fontWeight: 600,
  },
};

// ── Component ─────────────────────────────────────────────────

const PRINT_STYLES = `
@media print {
  /* Hide sidebar / aside */
  aside, .sidebar, nav[class*="sidebar"] {
    display: none !important;
  }

  /* Hide tab navigation and toolbar buttons */
  [data-print-hide] {
    display: none !important;
  }

  /* Show print header */
  [data-print-header] {
    display: block !important;
    text-align: center;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid #333;
  }

  [data-print-header] h2 {
    margin: 0 0 0.25rem 0;
    font-size: 1.4rem;
    color: #000;
  }

  [data-print-header] p {
    margin: 0;
    font-size: 0.85rem;
    color: #555;
  }

  /* Page layout */
  body {
    margin: 0;
    padding: 0;
  }

  /* Format tables for print */
  table {
    width: 100% !important;
    border-collapse: collapse !important;
    font-size: 10pt !important;
    page-break-inside: auto;
  }

  tr {
    page-break-inside: avoid;
    page-break-after: auto;
  }

  th, td {
    border: 1px solid #333 !important;
    padding: 4px 6px !important;
    text-align: left !important;
    font-size: 9pt !important;
  }

  th {
    background-color: #e8e8e8 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    font-weight: bold !important;
  }

  /* Page numbers */
  @page {
    margin: 1.5cm;
    @bottom-center {
      content: "Page " counter(page) " of " counter(pages);
      font-size: 9pt;
      color: #555;
    }
  }
}
`;

export function ReportsPage() {
  const [activeTab, setActiveTab] = useState<TabKey>('lists');
  const [activeReport, setActiveReport] = useState<ReportKey>('members');
  const [reportData, setReportData] = useState<unknown[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Analytics State ─────────────────────────────────────────
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);

  // ── Inject Print Styles ─────────────────────────────────────

  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.setAttribute('data-reports-print', 'true');
    styleEl.textContent = PRINT_STYLES;
    document.head.appendChild(styleEl);
    return () => {
      document.head.removeChild(styleEl);
    };
  }, []);

  // ── Data Fetching ───────────────────────────────────────────

  useEffect(() => {
    if (activeTab !== 'lists') return;

    let cancelled = false;

    async function fetchReportData() {
      setLoading(true);
      setError(null);
      setReportData(null);

      try {
        let data: unknown[];

        switch (activeReport) {
          case 'members':
            data = await apiClient<MemberRecord[]>('/members');
            data = (data as MemberRecord[]).sort((a, b) =>
              a.lastName.toLowerCase().localeCompare(b.lastName.toLowerCase())
            );
            break;
          case 'vendors':
            data = await apiClient<VendorRecord[]>('/vendors');
            data = (data as VendorRecord[]).sort((a, b) =>
              a.businessName.toLowerCase().localeCompare(b.businessName.toLowerCase())
            );
            break;
          case 'sponsors':
            data = await apiClient<SponsorRecord[]>('/sponsors');
            data = (data as SponsorRecord[]).sort((a, b) => {
              const rankA = TIER_RANK[a.tier] || 0;
              const rankB = TIER_RANK[b.tier] || 0;
              return rankB - rankA;
            });
            break;
          case 'users':
            data = await apiClient<UserRecord[]>('/users');
            data = (data as UserRecord[]).sort((a, b) => {
              const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
              const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
              return dateB - dateA;
            });
            break;
          default:
            data = [];
        }

        if (!cancelled) {
          setReportData(data);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load report data';
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchReportData();

    return () => {
      cancelled = true;
    };
  }, [activeReport, activeTab]);

  // ── Analytics Data Fetching ─────────────────────────────────

  useEffect(() => {
    if (activeTab !== 'analytics') return;

    let cancelled = false;

    async function fetchAnalytics() {
      setAnalyticsLoading(true);
      setAnalyticsError(null);

      try {
        const data = await apiClient<AnalyticsData>('/reports/analytics');
        if (!cancelled) {
          setAnalyticsData(data);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load analytics data';
          setAnalyticsError(message);
        }
      } finally {
        if (!cancelled) {
          setAnalyticsLoading(false);
        }
      }
    }

    fetchAnalytics();

    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  // ── Stub Handlers (implemented in subsequent tasks) ───────────

  function handlePrint() {
    window.print();
  }

  function handleExport() {
    if (!reportData || (reportData as unknown[]).length === 0) {
      // Export headers only for empty data
      const { headers } = getReportCsvMapping(activeReport, []);
      const filename = buildCsvFilename(`${activeReport}-report`);
      exportToCsv(filename, headers, []);
      return;
    }

    const { headers, rows } = getReportCsvMapping(activeReport, reportData as unknown[]);
    const filename = buildCsvFilename(`${activeReport}-report`);
    exportToCsv(filename, headers, rows);
  }

  function getReportCsvMapping(
    report: ReportKey,
    data: unknown[]
  ): { headers: string[]; rows: string[][] } {
    switch (report) {
      case 'members': {
        const members = data as MemberRecord[];
        const headers = ['Last Name', 'First Name', 'Email', 'Phone', 'Executive', 'Status', 'Subcommittees'];
        const rows = members.map((m) => [
          m.lastName,
          m.firstName,
          m.email,
          m.phone || '',
          m.executiveTitle?.name || '',
          m.status,
          m.subcommitteeAffiliations.map((a) => a.subcommitteeName).join('; '),
        ]);
        return { headers, rows };
      }
      case 'vendors': {
        const vendors = data as VendorRecord[];
        const headers = ['Business Name', 'Contact Person', 'Email', 'Phone', 'Category', 'Event Registrations', 'Total Payments'];
        const rows = vendors.map((v) => {
          const totalPayments = v.eventRegistrations
            ? v.eventRegistrations.reduce(
                (sum, reg) => sum + reg.payments.reduce((s, p) => s + Number(p.amount), 0),
                0
              )
            : 0;
          return [
            v.businessName,
            v.contactPerson,
            v.email,
            v.phone || '',
            v.category || '',
            v.eventRegistrations
              ? v.eventRegistrations.map((reg) => `${reg.event.name} (${reg.status})`).join('; ')
              : '',
            totalPayments > 0 ? `$${totalPayments.toFixed(2)}` : '',
          ];
        });
        return { headers, rows };
      }
      case 'sponsors': {
        const sponsors = data as SponsorRecord[];
        const headers = ['Sponsor Name', 'Contact Person', 'Tier', 'Pledged Amount', 'Received Amount', 'Outstanding', 'Events'];
        const rows = sponsors.map((s) => {
          const pledged = Number(s.pledgedAmount);
          const received = Number(s.receivedAmount);
          const outstanding = Math.max(0, pledged - received);
          return [
            s.sponsorName,
            s.contactPerson,
            s.tier,
            `$${pledged.toFixed(2)}`,
            `$${received.toFixed(2)}`,
            outstanding > 0 ? `$${outstanding.toFixed(2)}` : '$0.00',
            s.events ? s.events.map((e) => e.event.name).join('; ') : '',
          ];
        });
        return { headers, rows };
      }
      case 'users': {
        const users = data as UserRecord[];
        const headers = ['Full Name', 'Email', 'Role', 'Registration Date'];
        const rows = users.map((u) => [
          u.fullName,
          u.email,
          u.role,
          u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '',
        ]);
        return { headers, rows };
      }
      default:
        return { headers: [], rows: [] };
    }
  }

  // ── Report Table Renderers ────────────────────────────────────

  function renderMembersTable(data: MemberRecord[]) {
    if (data.length === 0) {
      return <div style={styles.emptyState}>No data available for this report</div>;
    }

    return (
      <table style={styles.table} className="reports-table">
        <thead>
          <tr>
            <th style={styles.th}>Last Name</th>
            <th style={styles.th}>First Name</th>
            <th style={styles.th}>Email</th>
            <th style={{ ...styles.th, width: '130px', minWidth: '130px', whiteSpace: 'nowrap' }}>Phone</th>
            <th style={{ ...styles.th, width: '150px', minWidth: '150px', whiteSpace: 'nowrap' }}>Executive</th>
            <th style={styles.th}>Status</th>
            <th style={styles.th}>Subcommittees</th>
          </tr>
        </thead>
        <tbody>
          {data.map((member) => (
            <tr key={member.id}>
              <td style={styles.td}>{member.lastName}</td>
              <td style={styles.td}>{member.firstName}</td>
              <td style={styles.td}>{member.email}</td>
              <td style={{ ...styles.td, width: '130px', minWidth: '130px', whiteSpace: 'nowrap' }}>{member.phone || '—'}</td>
              <td style={{ ...styles.td, width: '150px', minWidth: '150px', whiteSpace: 'nowrap' }}>{member.executiveTitle?.name || '—'}</td>
              <td style={styles.td}>{member.status}</td>
              <td style={styles.td}>
                {member.subcommitteeAffiliations.length > 0
                  ? member.subcommitteeAffiliations
                      .map((a) => a.subcommitteeName)
                      .join(', ')
                  : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  function renderVendorsTable(data: VendorRecord[]) {
    if (data.length === 0) {
      return <div style={styles.emptyState}>No data available for this report</div>;
    }

    return (
      <table style={styles.table} className="reports-table">
        <thead>
          <tr>
            <th style={styles.th}>Business Name</th>
            <th style={styles.th}>Contact Person</th>
            <th style={styles.th}>Email</th>
            <th style={styles.th}>Phone</th>
            <th style={styles.th}>Category</th>
            <th style={styles.th}>Event Registrations</th>
            <th style={styles.th}>Total Payments</th>
          </tr>
        </thead>
        <tbody>
          {data.map((vendor) => {
            const totalPayments = vendor.eventRegistrations
              ? vendor.eventRegistrations.reduce(
                  (sum, reg) =>
                    sum + reg.payments.reduce((s, p) => s + Number(p.amount), 0),
                  0
                )
              : 0;

            return (
              <tr key={vendor.id}>
                <td style={styles.td}>{vendor.businessName}</td>
                <td style={styles.td}>{vendor.contactPerson}</td>
                <td style={styles.td}>{vendor.email}</td>
                <td style={styles.td}>{vendor.phone || '—'}</td>
                <td style={styles.td}>{vendor.category || '—'}</td>
                <td style={styles.td}>
                  {vendor.eventRegistrations && vendor.eventRegistrations.length > 0
                    ? vendor.eventRegistrations
                        .map((reg) => `${reg.event.name} (${reg.status})`)
                        .join(', ')
                    : '—'}
                </td>
                <td style={styles.td}>
                  {totalPayments > 0 ? `$${totalPayments.toFixed(2)}` : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }

  function renderSponsorsTable(data: SponsorRecord[]) {
    if (data.length === 0) {
      return <div style={styles.emptyState}>No data available for this report</div>;
    }

    return (
      <table style={styles.table} className="reports-table">
        <thead>
          <tr>
            <th style={styles.th}>Sponsor Name</th>
            <th style={styles.th}>Contact Person</th>
            <th style={styles.th}>Tier</th>
            <th style={styles.th}>Pledged</th>
            <th style={styles.th}>Received</th>
            <th style={styles.th}>Outstanding</th>
            <th style={styles.th}>Events</th>
          </tr>
        </thead>
        <tbody>
          {data.map((sponsor) => {
            const pledged = Number(sponsor.pledgedAmount);
            const received = Number(sponsor.receivedAmount);
            const outstanding = Math.max(0, pledged - received);
            const hasOutstanding = received < pledged;

            return (
              <tr key={sponsor.id}>
                <td style={styles.td}>{sponsor.sponsorName}</td>
                <td style={styles.td}>{sponsor.contactPerson}</td>
                <td style={styles.td}>{sponsor.tier}</td>
                <td style={styles.td}>${pledged.toFixed(2)}</td>
                <td style={styles.td}>${received.toFixed(2)}</td>
                <td style={hasOutstanding ? { ...styles.td, ...styles.outstandingBalance } : styles.td}>
                  {hasOutstanding ? `$${outstanding.toFixed(2)}` : '$0.00'}
                </td>
                <td style={styles.td}>
                  {sponsor.events && sponsor.events.length > 0
                    ? sponsor.events.map((e) => e.event.name).join(', ')
                    : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }

  function renderUsersTable(data: UserRecord[]) {
    if (data.length === 0) {
      return <div style={styles.emptyState}>No data available for this report</div>;
    }

    return (
      <table style={styles.table} className="reports-table">
        <thead>
          <tr>
            <th style={styles.th}>Full Name</th>
            <th style={styles.th}>Email</th>
            <th style={styles.th}>Role</th>
            <th style={styles.th}>Registration Date</th>
          </tr>
        </thead>
        <tbody>
          {data.map((user) => (
            <tr key={user.id}>
              <td style={styles.td}>{user.fullName}</td>
              <td style={styles.td}>{user.email}</td>
              <td style={styles.td}>{user.role}</td>
              <td style={styles.td}>
                {user.createdAt
                  ? new Date(user.createdAt).toLocaleDateString()
                  : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  function renderReportContent() {
    if (loading) {
      return <div style={styles.loading}>Loading report data...</div>;
    }

    if (error) {
      return <div style={{ ...styles.emptyState, color: '#c0392b' }}>{error}</div>;
    }

    if (!reportData) {
      return <div style={styles.emptyState}>No data available for this report</div>;
    }

    switch (activeReport) {
      case 'members':
        return renderMembersTable(reportData as MemberRecord[]);
      case 'vendors':
        return renderVendorsTable(reportData as VendorRecord[]);
      case 'sponsors':
        return renderSponsorsTable(reportData as SponsorRecord[]);
      case 'users':
        return renderUsersTable(reportData as UserRecord[]);
      default:
        return null;
    }
  }

  // ── Tab Content Renderers ─────────────────────────────────────

  function renderReferenceListsTab() {
    const reportLabel = REPORTS.find(r => r.key === activeReport)?.label || '';

    return (
      <div>
        {/* Print Header (hidden on screen, shown on print) */}
        <div data-print-header style={{ display: 'none' }}>
          <h2>{reportLabel} Report</h2>
          <p>Generated on {new Date().toLocaleDateString()}</p>
        </div>

        {/* Report Sub-Selector */}
        <div style={styles.reportSelector} data-print-hide>
          {REPORTS.map(report => (
            <button
              key={report.key}
              style={activeReport === report.key ? styles.reportButtonActive : styles.reportButton}
              onClick={() => setActiveReport(report.key)}
            >
              <span>{report.icon}</span>
              <span>{report.label}</span>
            </button>
          ))}
        </div>

        {/* Report Toolbar */}
        <div style={styles.toolbar} data-print-hide>
          <button
            style={
              reportData
                ? styles.printButton
                : { ...styles.printButton, ...styles.buttonDisabled }
            }
            onClick={handlePrint}
            disabled={!reportData}
            title="Print report"
          >
            🖨️
          </button>
          <button
            style={
              reportData
                ? styles.exportButton
                : { ...styles.exportButton, ...styles.buttonDisabled }
            }
            onClick={handleExport}
            disabled={!reportData}
            title="Export as CSV"
          >
            📥
          </button>
        </div>

        {/* Report Content */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>
            {REPORTS.find(r => r.key === activeReport)?.label} Report
          </h3>
          {renderReportContent()}
        </div>
      </div>
    );
  }

  function renderAnalyticsTab() {
    if (analyticsLoading) {
      return <div style={styles.loading}>Loading analytics data...</div>;
    }

    if (analyticsError) {
      return (
        <div style={{ ...styles.emptyState, color: '#c0392b' }}>
          {analyticsError}
        </div>
      );
    }

    if (!analyticsData) {
      return <div style={styles.emptyState}>No analytics data available</div>;
    }

    return (
      <div>
        {renderFundraisingPanel()}
        {renderEventPanel()}
        {renderFinancialPanel()}
        {renderMembershipPanel()}
        {renderCommitteePanel()}
      </div>
    );
  }

  // ── Fundraising Analytics Panel (8.2) ─────────────────────────

  function renderFundraisingPanel() {
    if (!analyticsData) return null;
    const { fundraising } = analyticsData;

    return (
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Fundraising Analytics</h3>

        {/* Summary Row */}
        <div style={{ display: 'flex', gap: '2rem', marginBottom: '1.5rem', flexWrap: 'wrap' as const }}>
          <div>
            <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem' }}>Total Goal</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#0f2d1f' }}>
              ${fundraising.goalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem' }}>Total Raised</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#1a6b3c' }}>
              ${fundraising.totalRaised.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem' }}>Progress</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#1a6b3c' }}>
              {fundraising.progressPercent.toFixed(1)}%
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{
            width: '100%',
            height: '12px',
            backgroundColor: '#e0e0e0',
            borderRadius: '6px',
            overflow: 'hidden',
          }}>
            <div style={{
              width: `${Math.min(fundraising.progressPercent, 100)}%`,
              height: '100%',
              backgroundColor: '#1a6b3c',
              borderRadius: '6px',
              transition: 'width 0.3s ease',
            }} />
          </div>
        </div>

        {/* Breakdown by Type */}
        {fundraising.byType.length > 0 && (
          <div style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ fontSize: '0.95rem', color: '#0f2d1f', marginBottom: '0.75rem', fontWeight: 600 }}>
              By Activity Type
            </h4>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' as const }}>
              {fundraising.byType.map((item) => (
                <div key={item.type} style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#f0f7f2',
                  borderRadius: '6px',
                  border: '1px solid #d0e8d6',
                }}>
                  <div style={{ fontSize: '0.75rem', color: '#666' }}>{item.type}</div>
                  <div style={{ fontSize: '1rem', fontWeight: 600, color: '#1a6b3c' }}>
                    ${item.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Individual Activities */}
        {fundraising.activities.length > 0 && (
          <div>
            <h4 style={{ fontSize: '0.95rem', color: '#0f2d1f', marginBottom: '0.75rem', fontWeight: 600 }}>
              Activities
            </h4>
            <table style={styles.table} className="reports-table">
              <thead>
                <tr>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Goal</th>
                  <th style={styles.th}>Raised</th>
                </tr>
              </thead>
              <tbody>
                {fundraising.activities.map((activity) => (
                  <tr key={activity.id}>
                    <td style={styles.td}>{activity.name}</td>
                    <td style={styles.td}>{activity.type}</td>
                    <td style={styles.td}>${activity.goalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    <td style={styles.td}>${activity.raised.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // ── Event Analytics Panel (8.3) ───────────────────────────────

  function renderEventPanel() {
    if (!analyticsData) return null;
    const { events } = analyticsData;

    // Sort by date ascending
    const sortedEvents = [...events].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    return (
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Event Analytics</h3>
        {sortedEvents.length === 0 ? (
          <div style={styles.emptyState}>No event data available</div>
        ) : (
          <table style={styles.table} className="reports-table">
            <thead>
              <tr>
                <th style={styles.th}>Event Name</th>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Registrations</th>
                <th style={styles.th}>Capacity</th>
                <th style={styles.th}>Utilization</th>
              </tr>
            </thead>
            <tbody>
              {sortedEvents.map((event) => {
                const isNearCapacity = event.utilizationPercent > 90;
                const utilizationStyle: React.CSSProperties = {
                  ...styles.td,
                  color: isNearCapacity ? '#c0392b' : '#333',
                  fontWeight: isNearCapacity ? 600 : 400,
                  backgroundColor: isNearCapacity ? '#fdecea' : undefined,
                };

                return (
                  <tr key={event.id}>
                    <td style={styles.td}>{event.name}</td>
                    <td style={styles.td}>{new Date(event.date).toLocaleDateString()}</td>
                    <td style={styles.td}>{event.registrationCount}</td>
                    <td style={styles.td}>{event.maxCapacity}</td>
                    <td style={utilizationStyle}>
                      {event.utilizationPercent.toFixed(1)}%
                      {isNearCapacity && ' ⚠️'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  // ── Financial Summary Panel (8.4) ─────────────────────────────

  function renderFinancialPanel() {
    if (!analyticsData) return null;
    const { financial } = analyticsData;

    const revenueSources = [
      { label: 'Ticket Sales', amount: financial.ticketRevenue },
      { label: 'Vendor Payments', amount: financial.vendorRevenue },
      { label: 'Sponsor Payments', amount: financial.sponsorRevenue },
      { label: 'Booklet Sales', amount: financial.bookletRevenue },
      { label: 'Fundraising Activities', amount: financial.fundraisingRevenue },
    ];

    return (
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Financial Summary</h3>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem',
        }}>
          {revenueSources.map((source) => (
            <div key={source.label} style={{
              padding: '1rem',
              backgroundColor: '#f0f7f2',
              borderRadius: '8px',
              border: '1px solid #d0e8d6',
            }}>
              <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem' }}>
                {source.label}
              </div>
              <div style={{ fontSize: '1.1rem', fontWeight: 600, color: '#0f2d1f' }}>
                ${source.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </div>
            </div>
          ))}
        </div>

        {/* Grand Total */}
        <div style={{
          padding: '1rem 1.5rem',
          backgroundColor: '#1a6b3c',
          borderRadius: '8px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <span style={{ fontSize: '1rem', fontWeight: 600, color: '#fff' }}>Grand Total</span>
          <span style={{ fontSize: '1.4rem', fontWeight: 700, color: '#fff' }}>
            ${financial.grandTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </span>
        </div>
      </div>
    );
  }

  // ── Membership Analytics Panel (8.5) ──────────────────────────

  function renderMembershipPanel() {
    if (!analyticsData) return null;
    const { membership } = analyticsData;

    return (
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Membership Analytics</h3>

        {/* Counts and Dues Rate */}
        <div style={{ display: 'flex', gap: '2rem', marginBottom: '1.5rem', flexWrap: 'wrap' as const }}>
          <div>
            <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem' }}>Active Members</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#1a6b3c' }}>
              {membership.activeCount}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem' }}>Inactive Members</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#888' }}>
              {membership.inactiveCount}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem' }}>Dues Collection Rate</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#0f2d1f' }}>
              {membership.duesCollectionRate.toFixed(1)}%
            </div>
          </div>
        </div>

        {/* Dues Collection Progress Bar */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.4rem' }}>Dues Collection Progress</div>
          <div style={{
            width: '100%',
            height: '10px',
            backgroundColor: '#e0e0e0',
            borderRadius: '5px',
            overflow: 'hidden',
          }}>
            <div style={{
              width: `${Math.min(membership.duesCollectionRate, 100)}%`,
              height: '100%',
              backgroundColor: '#c9a84c',
              borderRadius: '5px',
              transition: 'width 0.3s ease',
            }} />
          </div>
        </div>

        {/* Chapter Distribution */}
        {membership.byChapter.length > 0 && (
          <div>
            <h4 style={{ fontSize: '0.95rem', color: '#0f2d1f', marginBottom: '0.75rem', fontWeight: 600 }}>
              Chapter Distribution
            </h4>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' as const }}>
              {membership.byChapter.map((chapter) => (
                <div key={chapter.chapter} style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#f0f7f2',
                  borderRadius: '6px',
                  border: '1px solid #d0e8d6',
                }}>
                  <div style={{ fontSize: '0.75rem', color: '#666' }}>{chapter.chapter}</div>
                  <div style={{ fontSize: '1rem', fontWeight: 600, color: '#1a6b3c' }}>
                    {chapter.count} members
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Committee Workload Panel (8.6) ────────────────────────────

  function renderCommitteePanel() {
    if (!analyticsData) return null;
    const { committees } = analyticsData;

    return (
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Committee Workload</h3>
        {committees.length === 0 ? (
          <div style={styles.emptyState}>No committee data available</div>
        ) : (
          <table style={styles.table} className="reports-table">
            <thead>
              <tr>
                <th style={styles.th}>Committee</th>
                <th style={styles.th}>Total Tasks</th>
                <th style={styles.th}>Completed</th>
                <th style={styles.th}>Completion %</th>
                <th style={styles.th}>Overdue</th>
              </tr>
            </thead>
            <tbody>
              {committees.map((committee) => {
                const isAtRisk = committee.overdueTasks > 3;
                const rowStyle: React.CSSProperties = isAtRisk
                  ? { backgroundColor: '#fdecea' }
                  : {};

                return (
                  <tr key={committee.id} style={rowStyle}>
                    <td style={styles.td}>{committee.name}</td>
                    <td style={styles.td}>{committee.totalTasks}</td>
                    <td style={styles.td}>{committee.completedTasks}</td>
                    <td style={styles.td}>{committee.completionPercent.toFixed(1)}%</td>
                    <td style={{
                      ...styles.td,
                      color: isAtRisk ? '#c0392b' : '#333',
                      fontWeight: isAtRisk ? 600 : 400,
                    }}>
                      {committee.overdueTasks}
                      {isAtRisk && ' ⚠️'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>Reporting &amp; Analytics</h1>
        <p style={styles.description}>
          Generate printable reference lists, export data, and view aggregated insights across all operational areas.
        </p>
      </div>

      {/* Tab Navigation */}
      <div style={styles.tabBar} data-print-hide>
        {TABS.map(tab => (
          <button
            key={tab.key}
            style={activeTab === tab.key ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'lists' && renderReferenceListsTab()}
      {activeTab === 'analytics' && renderAnalyticsTab()}
    </div>
  );
}
